import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { Form, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';

import { getCommunity } from 'apis/CommunityAPI';
import { getUnits } from 'apis/UnitsAPI';
import DataPropagationConfirmModal from 'common/Modal/DataPropagationConfirmModal';
import { SNAPSHOT_SELECTOR_SELECTION } from 'common/Snapshot/editor/constants';
import { useSnapshotContext } from 'common/Snapshot/SnapshotContext';
import { isCommunityCCRC } from 'components/Community/utils';
import { useCommunityInfoContext } from 'components/CommunityInfo/CommunityInfoContext';
import { useRootContext } from 'components/layout/RootContext';
import { isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { CommunityContract } from 'types/contracts';
import { SnapshotDataType } from 'types/snapshot';

import { INITIAL_SNAPSHOT_DATA_STATUS, SHOP_DATA_TABS_GENERAL_KEY } from './constants';
import { hasPendingDocumentAnalysis } from './documents/helpers';
import { createSnapshotData, resolveTabStep, willDuplicateSnapshot } from './helpers';
import { useSnapshotData } from './hooks';
import { SHOP_DATA_DEFAULT_MENU_ITEM_INDEX } from './menus';
import ShopDataModalSavePromptModal from './prompt/ShopDataModalSavePromptModal';
import { ShopDataModalContext } from './ShopDataModalContext';
import ShopDataModalHeader from './ShopDataModalHeader';
import ShopDataModalLayout from './ShopDataModalLayout';
import ShopDataModalTabs from './ShopDataModalTabs';
import { SnapshotDataStatusType } from './types';

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-content {
      padding: 0px;
      line-height: 1;
      border-radius: 1px;
      user-select: none;
      height: 100%;
    }
    .ant-modal-body {
      display: flex;
      flex-direction: column;
      padding: 0px;
      height: 100%;
      line-height: normal;
    }
  }
`;

const ShopDataContent = styled.div`
  display: flex;
  width: 100%;
  height: calc(100vh - 300px);
  flex-grow: 1;
`;

const extractErrorMessage = (errorData: any): string => {
  if (!errorData) {
    return 'Issues encountered while submitting Snapshot data!';
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (Array.isArray(errorData)) {
    for (const item of errorData) {
      const message = extractErrorMessage(item);
      if (message) {
        return message;
      }
    }

    return 'Issues encountered while submitting Snapshot data!';
  }

  if (typeof errorData === 'object') {
    for (const value of Object.values(errorData)) {
      const message = extractErrorMessage(value);
      if (message) {
        return message;
      }
    }
  }

  return 'Issues encountered while submitting Snapshot data!';
};

type Props = {
  open: boolean;
  communityId: number;
  snapshot?: SnapshotDataType;
  draft?: boolean;
  disabled?: boolean;
  shopRequestId?: number;
  date: Dayjs;
  mode: number;
  onClose: () => void;
  onCreate: (value: any) => void;
};

const ShopDataModal = ({
  open,
  communityId,
  snapshot,
  date,
  mode,
  draft = false,
  disabled,
  shopRequestId,
  onCreate,
  onClose,
}: Props) => {
  const { livingTypes, apartmentTypeGroups } = useAppSelector((state) => state.global);
  const { currentUser } = useAppSelector((state) => state.auth);

  const { showMessage } = useRootContext();
  const { currentSnapshot } = useSnapshotContext();
  const { communityInfo, unitSource: communityUnitSource } = useCommunityInfoContext();

  const [currentTab, setCurrentTab] = useState<string>(SHOP_DATA_TABS_GENERAL_KEY);
  const [currentStep, setCurrentStep] = useState<number>(SHOP_DATA_DEFAULT_MENU_ITEM_INDEX);
  const [currentLivingType, setCurrentLivingType] = useState<any>(undefined);
  const [currentData, setCurrentData] = useState<any>(undefined);
  const [currentContract, setCurrentContract] = useState<CommunityContract | undefined>(undefined);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [dataStatus, setDataStatus] = useState<SnapshotDataStatusType>(INITIAL_SNAPSHOT_DATA_STATUS);
  const [form] = Form.useForm();

  const { data: initialData, loading } = useSnapshotData(communityId, snapshot, mode);
  const { data: units } = useQuery({
    queryKey: ['community', 'units', communityId],
    queryFn: () => getUnits(communityId),
    refetchOnWindowFocus: false,
    enabled: Boolean(communityId),
  });
  const { data: fetchedCommunity } = useQuery({
    queryKey: ['shopDataModal', 'community', communityId],
    queryFn: () => getCommunity(communityId),
    enabled: Boolean(communityId && !communityInfo),
  });

  const community = communityInfo || fetchedCommunity;
  const unitSource = communityUnitSource ?? community;
  const isCommunityCcrc = isCommunityCCRC(community?.rental_types || []);

  useEffect(() => {
    setCurrentData(initialData);
  }, [initialData]);

  useEffect(() => {
    const selectedLivingType = livingTypes.find((item: any) => item?.id === Number(currentTab));
    setCurrentLivingType(selectedLivingType);
  }, [currentTab]);

  const validateData = async (onSuccess: () => void, onError: () => void) => {
    await form
      .validateFields({ recursive: true })
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        onError();
        showMessage('info', 'Please verify your data entry is valid.');
      });
  };

  const handleCancel = async (e: any) => {
    const hasDirtyData = Object.values(dataStatus).some((item: any) => item?.dirty);
    if (hasDirtyData) {
      setShowSavePrompt(true);
    } else {
      onClose();
    }
  };

  const handleDataStatusChanged = (categories: string[]) => {
    setDataStatus((prevState: any) => {
      const updatedStatus = { ...prevState };

      categories.forEach((category) => {
        updatedStatus[category] = { ...prevState[category], dirty: true };
      });

      return updatedStatus;
    });
  };

  const handleSubmit = async () => {
    if (hasPendingDocumentAnalysis(currentData?.documents ?? [])) {
      showMessage('info', 'Please wait until document analysis finishes before saving.');
      return;
    }

    const isAdmin = currentUser?.isSuperuser || isManagedBySuperuser(currentUser);
    // Duplicate saves create a company-owned snapshot, so the propagation confirmation does not apply.
    const shouldShowPropagationConfirmation =
      snapshot?.shared && !snapshot.draft && isAdmin && !willDuplicateSnapshot(snapshot, currentUser);

    if (shouldShowPropagationConfirmation) {
      setShowConfirmationModal(true);
    } else {
      await handleSubmitData();
    }
  };

  const handleSubmitData = async () => {
    setRequestLoading(true);
    const isLatestSnapshotEntry = [
      SNAPSHOT_SELECTOR_SELECTION.ENTER_NEW_DATA,
      SNAPSHOT_SELECTOR_SELECTION.MODIFY_CURRENT_DATA,
    ].includes(mode);
    const isLatestSnapshotDate = !currentSnapshot || dayjs(date).isAfter(currentSnapshot?.date);

    let latest;
    if (mode !== SNAPSHOT_SELECTOR_SELECTION.ENTER_SHOP_DATA) {
      latest = isLatestSnapshotEntry || isLatestSnapshotDate;
    } else {
      latest = false;
    }
    const snapshotPayload = {
      ...snapshot,
      community: communityId,
      date,
      latest,
      shop_request_id: shopRequestId,
      draft: snapshot?.draft || draft,
    };

    try {
      const responseSnapshot = await createSnapshotData(snapshotPayload, currentData, currentUser, apartmentTypeGroups);
      onCreate(responseSnapshot);
      showMessage('success', 'Data saved.');
    } catch (e: any) {
      const errorMessage = extractErrorMessage(e?.response?.data);
      showMessage('error', errorMessage);
    }
    setRequestLoading(false);
  };

  const handleNavigation = async (increment: number) => {
    await validateData(
      () => {
        form.submit();
        const { tab, step } = resolveTabStep(currentTab, currentStep, increment, isCommunityCcrc, community);
        setCurrentTab(tab);
        setCurrentStep(step);
      },
      () => {
        // Does Nothing
      }
    );
  };

  return (
    <StyledModal
      open={open}
      width={window.innerWidth - 120}
      style={{
        height: `calc(100vh - 120px)`, // 60px padding from top and bottom
        padding: 0, // Ensure no extra padding from the Modal itself
        overflow: 'hidden',
      }}
      footer={null}
      onCancel={handleCancel}
    >
      <ShopDataModalContext.Provider
        value={{
          form,
          loading,
          mode,
          community,
          unitSource,
          communityId,
          snapshot,
          date,
          currentData,
          currentTab,
          currentStep,
          currentLivingType,
          currentContract,
          dataStatus,
          units,
          requestLoading,
          disabled,
          isCommunityCcrc,
          setCurrentTab,
          setCurrentStep,
          setCurrentData,
          setRequestLoading,
          setCurrentContract,
          validateData,
          handleDataStatusChanged,
          handleNavigation,
          handleSubmit,
        }}
      >
        <ShopDataModalHeader />
        <ShopDataModalTabs />
        <ShopDataContent>
          {showSavePrompt && (
            <ShopDataModalSavePromptModal
              onConfirm={handleSubmit}
              onClose={onClose}
              onCancel={() => setShowSavePrompt(false)}
            />
          )}
          {showConfirmationModal && (
            <DataPropagationConfirmModal
              open={showConfirmationModal}
              onConfirm={handleSubmitData}
              onCancel={() => setShowConfirmationModal(false)}
            />
          )}
          <ShopDataModalLayout />
        </ShopDataContent>
      </ShopDataModalContext.Provider>
    </StyledModal>
  );
};

export default ShopDataModal;
